import { defineMessages } from '@faceup/localization'
import { FrontendPermissionType } from '@faceup/utils'

export const sharedMessages = defineMessages('institution.shared', [
  {
    id: 'functionNotReady',
    defaultMessage: 'This feature is being prepared and will be available soon',
  },
  { id: 'apiError', defaultMessage: 'An error occurred. Please try again.' },
  { id: 'savedMessage', defaultMessage: 'Changes have been saved' },
  { id: 'encryptionError', defaultMessage: 'Encryption error' },
  { id: 'attachmentsTooManyFiles', defaultMessage: 'You can upload a maximum of 20 attachments.' },
  {
    id: 'attachmentsTooLargeFile',
    defaultMessage: 'The file you are trying to upload is too large. Maximum file size is 50 MB.',
  },
  { id: 'metadataRemoved', defaultMessage: 'Metadata have been removed' },
  { id: 'metadataUnableToRemove', defaultMessage: 'Metadata have not been removed' },
  {
    id: 'unsupportedMimetype',
    defaultMessage:
      'We do not support this file type. Please, upload a file in JPG, PNG, PDF, DOC, DOCX, ODT, ODS, XLS, XLSX, TXT, MOV, MP3, MP4, AVI or WAV.',
  },
  { id: 'upgradeButton', defaultMessage: 'Find out more' },
  {
    id: 'permissionEditableOnlyMotherTooltip',
    defaultMessage: 'Only members in the mother organization can perform this action.',
  },
  { id: 'invalidNameError', defaultMessage: 'Please enter your name and surname' },
  { id: 'invalidEmailError', defaultMessage: 'Invalid email' },
  { id: 'invalidPhoneError', defaultMessage: 'Enter a valid phone number' },
  { id: 'invalidPasswordError', defaultMessage: 'Password contains invalid characters' },
  { id: 'invalidAddressError', defaultMessage: 'Invalid address' },
  { id: 'passwordsDontMatchError', defaultMessage: 'These passwords don’t match' },
  { id: 'invalidInputError', defaultMessage: 'This field is required' },
  { id: 'emailLabel', defaultMessage: 'Email' },
  { id: 'nameLabel', defaultMessage: 'Name' },
  { id: 'bioLabel', defaultMessage: 'Bio' },
  { id: 'fullNameLabel', defaultMessage: 'Name and surname' },
  { id: 'phoneLabel', defaultMessage: 'Phone number' },
  { id: 'passwordLabel', defaultMessage: 'Password' },
  { id: 'organizationLabel', defaultMessage: 'Organizational unit' },
  { id: 'categoryLabel', defaultMessage: 'Category' },
  { id: 'moreInfoLabel', defaultMessage: 'More information' },
  { id: 'classNameLabel', defaultMessage: 'Class' },
  { id: 'globalNameLabel', defaultMessage: 'Identity of the sender' },
  { id: 'bio', defaultMessage: '{name}' },
  { id: 'partnerBio', defaultMessage: '{name}' },
  { id: 'send', defaultMessage: 'Send' },
  { id: 'delete', defaultMessage: 'Delete' },
  { id: 'save', defaultMessage: 'Save' },
  { id: 'next', defaultMessage: 'Next' },
  { id: 'enable', defaultMessage: 'Enable' },
  { id: 'disable', defaultMessage: 'Disable' },
  { id: 'edit', defaultMessage: 'Edit' },
  { id: 'copy', defaultMessage: 'Copy' },
  { id: 'print', defaultMessage: 'Print' },
  { id: 'show', defaultMessage: 'Show' },
  { id: 'download', defaultMessage: 'Download' },
  { id: 'backHome', defaultMessage: 'Return to home page' },
  { id: 'add', defaultMessage: 'Add' },
  { id: 'removeFilter', defaultMessage: 'Remove filter' },
  { id: 'back', defaultMessage: 'Back' },
  { id: 'reset', defaultMessage: 'Reset' },
  { id: 'ok', defaultMessage: 'OK' },
  { id: 'cancel', defaultMessage: 'Cancel' },
  { id: 'sendAnonymously', defaultMessage: 'Send the case anonymously' },
  { id: 'copied', defaultMessage: 'Copied' },
  {
    id: 'leaveQuestion',
    defaultMessage:
      'You are about to leave a page on which you have unsaved changes. We recommend you save your changes, otherwise you will lose them.',
  },
] as const)

export const permissionTypeMessages = defineMessages(
  'institution.permissionType',
  FrontendPermissionType,
  {
    [FrontendPermissionType.ReportAccess]: {
      title: 'View cases',
      hint: 'Access to cases based on the criteria selected below.',
    },
    [FrontendPermissionType.SettingsAccess]: {
      title: 'General settings',
      hint: 'Access to the Organization settings page. The member can change the organization name, automatic due date and encryption settings, or download log records.',
    },
    [FrontendPermissionType.BillingAccess]: {
      title: 'Manage billing',
      hint: 'Access to the Billing page. The member can access the price list, purchase a plan, change billing information, and view billing history. A member with this access can also purchase hotline credits.',
    },
    [FrontendPermissionType.ManageCategories]: {
      title: 'Manage data management',
      hint: 'Access to categories page (member can create, delete, edit or reorder categories), statuses page (member can create, delete, edit or reorder statuses) and employees section (for integrations with BambooHR or UKG).',
    },
    [FrontendPermissionType.ManageOrganizationalUnits]: {
      title: 'Manage organizational units',
      hint: 'Access to the Organizational structure page. A member can create and delete organizational units or edit their name.',
    },
    [FrontendPermissionType.ManageReportingChannels]: {
      title: 'Manage channels',
      hint: 'Access to the form and hotline settings. A member can create, delete and modify all channels settings.',
    },
    [FrontendPermissionType.ManageUsers]: {
      title: 'Manage members',
      hint: 'Access the Members page. A member can add and delete members and edit their access settings.',
    },
    [FrontendPermissionType.Analytics]: {
      title: 'Analytics',
      hint: null,
    },
    [FrontendPermissionType.EditCases]: {
      title: 'Manage cases',
      hint: 'A member can edit cases (change category, organizational unit, etc.), add assignees, add labels, manage due dates, set priority and justification, and manage the "Who has access" section.',
    },
    [FrontendPermissionType.ExportCases]: {
      title: 'Export and import of cases',
      hint: 'A member can import, export or print cases.',
    },
    [FrontendPermissionType.DeleteCases]: {
      title: 'Delete cases',
      hint: 'A member can delete cases, if this function is enabled in the organization’s settings.',
    },
    [FrontendPermissionType.WriteInternalComments]: {
      title: 'Write internal comments',
      hint: 'A member can write internal comments.',
    },
    [FrontendPermissionType.WriteMessages]: {
      title: 'Write messages',
      hint: 'A member can write messages to a sender.',
    },
    [FrontendPermissionType.Surveys]: {
      title: 'Surveys',
      hint: null,
    },
    [FrontendPermissionType.InvestigationAccess]: {
      title: 'Investigations',
      hint: 'Access to assigned investigations.',
    },
    [FrontendPermissionType.DisclosureAccess]: {
      title: 'Disclosures',
      hint: null,
    },
  }
)
